/* eslint-disable max-lines-per-function */
import {media, Spacer, spacing} from '@nfq/react-grid';
import {useTranslation} from 'next-i18next';
import styled from 'styled-components';

import {Text, TextHighlight} from 'UI/components/layout/ultra/Text';

/**
 * Renders a base grid displaying various basic infos about the cockpit, frame, fork, seatpost, and seat.
 * Each component is accompanied by its respective headline, highlight, and description points retrieved from translations.
 *
 * @returns The JSX element representing the base grid.
 *
 * @example
 * ```tsx
 * <BaseGrid />
 * ```
 */
export const BaseGrid = () => {
    const {t} = useTranslation('ultra', {keyPrefix: 'details.specs.base'});

    return (
        <ItemGrid>
            <GridItem>
                <Text>
                    <UppercaseHighlight>{t('items.cockpit.headline')}</UppercaseHighlight>
                    <Spacer y={{lg: 8, xs: 4}} />
                </Text>
                <div>
                    <ValueHeadline>{t('items.cockpit.highlight')}</ValueHeadline>
                    <ValueWrapper>
                        <TextLi>{t('items.cockpit.point1')}</TextLi>
                        <TextLi>{t('items.cockpit.point2')}</TextLi>
                        <TextLi>{t('items.cockpit.point3')}</TextLi>
                        <TextLi>{t('items.cockpit.point4')}</TextLi>
                        <TextLi>{t('items.cockpit.point5')}</TextLi>
                        <TextLi>{t('items.cockpit.point6')}</TextLi>
                        <TextLi>{t('items.cockpit.point7')}</TextLi>
                        <TextLi>{t('items.cockpit.point8')}</TextLi>
                        <TextLi>{t('items.cockpit.point9')}</TextLi>
                        <TextLi>{t('items.cockpit.point10')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridItem>
            <GridItem>
                <Text>
                    <UppercaseHighlight>{t('items.frame.headline')}</UppercaseHighlight>
                    <Spacer y={{lg: 8, xs: 4}} />
                </Text>
                <div>
                    <ValueHeadline>{t('items.frame.highlight')}</ValueHeadline>
                    <ValueWrapper>
                        <TextLi>{t('items.frame.point1')}</TextLi>
                        <TextLi>{t('items.frame.point2')}</TextLi>
                        <TextLi>{t('items.frame.point3')}</TextLi>
                        <TextLi>{t('items.frame.point4')}</TextLi>
                        <TextLi>{t('items.frame.point5')}</TextLi>
                        <TextLi>{t('items.frame.point6')}</TextLi>
                        <TextLi>{t('items.frame.point7')}</TextLi>
                        <TextLi>{t('items.frame.point8')}</TextLi>
                        <TextLi>{t('items.frame.point9')}</TextLi>
                        <TextLi>{t('items.frame.point10')}</TextLi>
                        <TextLi>{t('items.frame.point11')}</TextLi>
                        <TextLi>{t('items.frame.point12')}</TextLi>
                        <TextLi>{t('items.frame.point13')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridItem>
            <GridItem>
                <Text>
                    <UppercaseHighlight>{t('items.fork.headline')}</UppercaseHighlight>
                    <Spacer y={{lg: 8, xs: 4}} />
                </Text>
                <div>
                    <ValueHeadline>{t('items.fork.highlight')}</ValueHeadline>
                    <ValueWrapper>
                        <TextLi>{t('items.fork.point1')}</TextLi>
                        <TextLi>{t('items.fork.point2')}</TextLi>
                        <TextLi>{t('items.fork.point3')}</TextLi>
                        <TextLi>{t('items.fork.point4')}</TextLi>
                        <TextLi>{t('items.fork.point5')}</TextLi>
                        <TextLi>{t('items.fork.point6')}</TextLi>
                        <TextLi>{t('items.fork.point7')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridItem>
            <GridItem>
                <Text>
                    <UppercaseHighlight>{t('items.seatpost.headline')}</UppercaseHighlight>
                    <Spacer y={{lg: 8, xs: 4}} />
                </Text>
                <div>
                    <ValueHeadline>{t('items.seatpost.highlight')}</ValueHeadline>
                    <ValueWrapper>
                        <TextLi>{t('items.seatpost.point1')}</TextLi>
                        <TextLi>{t('items.seatpost.point2')}</TextLi>
                        <TextLi>{t('items.seatpost.point3')}</TextLi>
                        <TextLi>{t('items.seatpost.point4')}</TextLi>
                        <TextLi>{t('items.seatpost.point5')}</TextLi>
                        <TextLi>{t('items.seatpost.point6')}</TextLi>
                        <TextLi>{t('items.seatpost.point7')}</TextLi>
                        <TextLi>{t('items.seatpost.point8')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridItem>
            <GridItem>
                <Text>
                    <UppercaseHighlight>{t('items.seat.headline')}</UppercaseHighlight>
                    <Spacer y={{lg: 8, xs: 4}} />
                </Text>
                <div>
                    <ValueHeadline>{t('items.seat.highlight')}</ValueHeadline>
                    <ValueWrapper>
                        <TextLi>{t('items.seat.point1')}</TextLi>
                        <TextLi>{t('items.seat.point2')}</TextLi>
                        <TextLi>{t('items.seat.point3')}</TextLi>
                        <TextLi>{t('items.seat.point4')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridItem>
            <GridItem>
                <Text>
                    <UppercaseHighlight>{t('items.headset.headline')}</UppercaseHighlight>
                    <Spacer y={{lg: 8, xs: 4}} />
                </Text>
                <div>
                    <ValueHeadline>{t('items.headset.highlight')}</ValueHeadline>
                    <ValueWrapper>
                        <TextLi>{t('items.seat.point1')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridItem>
            <GridItem>
                <Text>
                    <UppercaseHighlight>{t('items.bolts.headline')}</UppercaseHighlight>
                    <Spacer y={{lg: 8, xs: 4}} />
                </Text>
                <div>
                    <ValueHeadline>{t('items.bolts.highlight')}</ValueHeadline>
                    <ValueWrapper>
                        <TextLi>{t('items.bolts.point1')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridItem>
        </ItemGrid>
    );
};

const ItemGrid = styled.div`
    column-gap: ${spacing(5)};
    display: grid;
    grid-auto-rows: max-content max-content 3.2rem;
    grid-template-columns: 1fr;
    width: 100%;

    ${media('md')} {
        grid-auto-rows: max-content max-content 6.4rem;
        grid-template-columns: 1fr 1fr;
    }

    ${media('lg')} {
        grid-template-columns: 1fr 1fr 1fr;
    }
`;

const GridItem = styled.div`
    display: grid;
    grid-row: span 3;
    grid-template-rows: subgrid;
    min-width: 0;
    padding-inline: ${spacing(4)};
`;

const ValueHeadline = styled(Text)`
    display: block;
    font-weight: 700;
    margin: 0;
`;

const ValueWrapper = styled.ul`
    margin: 0;
    padding-inline-start: 3rem;
`;

const TextLi = styled(Text).attrs({as: 'li'})`
    font-size: 1.4rem;
    line-height: 1.8rem;

    ${media('lg')} {
        font-size: 2rem;
        line-height: 2.6rem;
    }
`;

const UppercaseHighlight = styled(TextHighlight)`
    text-transform: uppercase;
`;